import moment from 'moment';
import Immutable from 'immutable';

const WHEN_PREVIOUS_MODULE_COMPLETED = 'when previous module is completed';

export const getModuleCompletionData = (page) => {
  const p = page;
  p.numberSectionsCompleted = p.hasUserCommented
    ? p.completed + 1
    : p.completed;

  // commenting is not a block activity but can contribute to progress
  p.numberOfSections = p.isCommentRequired
    ? p.completion_required + 1
    : p.completion_required;

  p.areSectionsCompleted = p.numberSectionsCompleted === p.numberOfSections;
  p.sectionsFraction = `${p.numberSectionsCompleted}/${p.numberOfSections}`;

  return p;
};

export const getModulePageDisplayData = (
  page,
  schedulingList,
  forProgressPage
) => {
  const p = getModuleCompletionData(page);

  let sl = schedulingList.get(page.path);
  if (sl) {
    sl = sl.toJS();

    p.isDueDateEnable = sl.isDueDateEnable;
    p.dueDate = moment(sl.dueDate).format('DD/MM/YYYY');
    p.accessTimeFrom = moment(sl.accessTimeFrom).format('DD/MM/YYYY');
    p.accessTimeTo = moment(sl.accessTimeTo).format('DD/MM/YYYY');
    p.accessTimeContext = null;
    p.accessDate = null;

    if (sl.accessTimeFrom !== null && moment().isBefore(sl.accessTimeFrom)) {
      p.accessTimeContext = 'This activity is closed';
      p.accessDate = `Starts: ${p.accessTimeFrom}`;
      p.isOpen = false;
      p.isClose = false;
    } else if (sl.accessTimeTo !== null && moment().isAfter(sl.accessTimeTo)) {
      p.accessTimeContext = 'This activity was closed';
      p.accessDate = `Closed: ${p.accessTimeTo}`;
      p.isOpen = true;
      p.isClose = true;
    }

    if (forProgressPage) {
      p.lastCompletedAt = null;
      p.isOverdue = false;
      if (sl.isDueDateEnable && moment().isAfter(sl.dueDate)) {
        if (p.areSectionsCompleted) {
          if (p.completionDate) {
            const completionDate = moment(p.completionDate);
            p.lastCompletedAt = completionDate.format('DD/MM/YYYY');
            p.isOverdue = completionDate.isAfter(sl.dueDate);
          }
        } else {
          p.isOverdue = true;
        }
      }
    }
  }

  if (forProgressPage) {
    const hasSections = !!p.numberOfSections;
    const hasSomeCompleted = !!p.numberSectionsCompleted;
    p.notStarted = hasSections && !hasSomeCompleted;
    p.inProgress = hasSections && hasSomeCompleted && !p.areSectionsCompleted;
    p.hasCompleted = hasSections && hasSomeCompleted && p.areSectionsCompleted;
  }

  return p;
};

export const getModuleCompletion = (mdl) => {
  const mandatoryPages = mdl.pages.filter((p) => p.isProgressContributor);
  const optionalPages = mdl.pages.filter((p) => !p.isProgressContributor);

  const numMandatoryCompleted = mandatoryPages.filter((p) => {
    return p.numberSectionsCompleted === p.numberOfSections;
  }).length;
  const totalMandatoryPages = mandatoryPages.length;

  const numOptionalCompleted = optionalPages.filter((p) => {
    return p.numberSectionsCompleted === p.numberOfSections;
  }).length;
  const totalOptionalPages = optionalPages.length;

  let hasCompleted = false;
  if (totalMandatoryPages === 0) {
    // No mandatory pages, just use optional pages for completion
    hasCompleted =
      totalOptionalPages !== 0 && numOptionalCompleted === totalOptionalPages;
  } else {
    // Has mandatory pages, just use those
    hasCompleted =
      totalMandatoryPages !== 0 &&
      numMandatoryCompleted === totalMandatoryPages;
  }

  return {
    numMandatoryCompleted,
    totalMandatoryPages,
    numOptionalCompleted,
    totalOptionalPages,
    hasCompleted,
  };
};

export const getSeenIncompleteModule = (moduleList, mdl) => {
  let seenIncompleteModule = false;

  // eslint-disable-next-line no-restricted-syntax
  for (const m of moduleList) {
    if (m.id === mdl.id) {
      // we've found this module
      break;
    }

    const { hasCompleted } = getModuleCompletion(m);
    if (!hasCompleted) {
      // we only want to know if at least one of the previous modules is incomplete.
      seenIncompleteModule = true;
      break;
    }
  }

  return seenIncompleteModule;
};

export const getModuleDisplayData = (
  moduleList,
  modulesInSet,
  schedule,
  moduleStartDates = Immutable.Map(),
  schedulingList = Immutable.Map(),
  isCourseAdminOrStaff = false,
  forProgressPage = false
) => {
  const firstModule = moduleList ? moduleList[0] : { id: null };
  // need to make all pages in module have the completion information
  const allModules = moduleList.map((mdl) => {
    const m = mdl;
    m.pages = mdl.pages.map((p) => getModuleCompletionData(p));
    return m;
  });
  // check each module in this module set.
  return modulesInSet.map((mdl) => {
    const m = mdl;
    m.pages = mdl.pages.map((p) =>
      getModulePageDisplayData(p, schedulingList, forProgressPage)
    );

    const {
      numMandatoryCompleted,
      totalMandatoryPages,
      hasCompleted,
    } = getModuleCompletion(m);

    m.progress = { numMandatoryCompleted, totalMandatoryPages };
    m.hasCompleted = hasCompleted;
    m.showPages = false;
    m.availableDate = null;
    m.isStarted = true;
    if (moduleStartDates.has(m.id)) {
      m.isStarted = !moment(moduleStartDates.get(m.id)).isAfter();
    }

    if (schedule.isScheduled) {
      const startDate = moment(moduleStartDates.get(m.id));
      if (schedule.isSelfPaced) {
        if (startDate.isAfter()) {
          // start date is in the future
          if (m.id === firstModule.id) {
            // the first module cannot be accessed
            m.showPages = false;
          } else {
            // this module can only be shown if all previous modules are complete
            const seenIncompleteModule = getSeenIncompleteModule(allModules, m);
            m.showPages = !seenIncompleteModule;
            if (m.showPages) {
              // need to update all m.pages to remove the accessTimeContext so the ModuleDisplay/Page/Thumbnail doesn't have a closed overlay.
              m.pages = m.pages.map((pg) => {
                const p = pg;
                p.accessTimeContext = null;
                return p;
              });
            }
          }
        } else {
          // the start date is in the past
          m.showPages = true;
        }
      } else {
        m.showPages = !startDate.isAfter();
      }
      m.availableDate = startDate.format('dddd, Do MMMM (h:mma)');
    } else if (schedule.isOpen) {
      if (schedule.startDate) {
        const startDate = moment(schedule.startDate);
        m.showPages = !startDate.isAfter();
        m.availableDate = startDate.format('dddd, Do MMMM (h:mma)');
      } else {
        m.showPages = true;
      }
    } else if (schedule.isSelfPaced) {
      if (schedule.startDate) {
        const startDate = moment(schedule.startDate);

        if (startDate.isAfter()) {
          // the start date is still in the future
          m.showPages = false;
          // the first module of all the modules should show the start date.
          if (m.id === firstModule.id) {
            m.availableDate = startDate.format('dddd, Do MMMM (h:mma)');
          } else {
            m.availableDate = WHEN_PREVIOUS_MODULE_COMPLETED;
          }
        } else {
          // the start date is in the past
          const seenIncompleteModule = getSeenIncompleteModule(allModules, m);
          m.showPages = !seenIncompleteModule;
          if (seenIncompleteModule) {
            m.availableDate = WHEN_PREVIOUS_MODULE_COMPLETED;
          } else if (m.id === firstModule.id) {
            m.availableDate = startDate.format('dddd, Do MMMM (h:mma)');
          }
        }
      } else {
        // there is no start date
        const seenIncompleteModule = getSeenIncompleteModule(allModules, m);
        m.showPages = !seenIncompleteModule;
        if (seenIncompleteModule) {
          m.availableDate = WHEN_PREVIOUS_MODULE_COMPLETED;
        }
      }
    }

    m.showPages = m.showPages || isCourseAdminOrStaff;

    return m;
  });
};

export const getModulesBySet = (
  moduleList,
  defaultModuleSetName = 'Default'
) => {
  let modulesBySet = Immutable.Map();
  moduleList.forEach((module) => {
    const setName = module.get('setName') || defaultModuleSetName;
    const moduleSet = modulesBySet.get(setName, Immutable.List());
    modulesBySet = modulesBySet.set(setName, moduleSet.push(module));
  });
  return modulesBySet;
};
